import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, fileUrl, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { playChime, primeAudio } from "@/lib/chime";
import { downloadForStories } from "@/lib/storyExport";
import {
  Upload,
  QrCode,
  Download,
  Copy,
  ExternalLink,
  Calendar,
  Camera,
  Sparkles,
  Clock,
  Info,
  DollarSign,
  ArrowLeft,
  Tv,
  Instagram,
  Volume2,
  VolumeX,
  Trophy,
  FileText,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import TestimonialModal from "@/components/TestimonialModal";

export default function EventManagePage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pix, setPix] = useState(null);
  const [qrLoaded, setQrLoaded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [newIds, setNewIds] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [testimonialOpen, setTestimonialOpen] = useState(false);
  const prevIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const framePreviewRef = useRef(null);

  const loadEvent = () => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data));
    api.get(`/public/events/__any__`).catch(() => {}); // noop
  };

  const loadPhotos = async () => {
    if (!event?.slug) return;
    try {
      const { data } = await api.get(`/public/events/${event.slug}/photos`);
      const currentIds = new Set(data.map((p) => p.id));
      if (!firstLoadRef.current) {
        const arrivals = data
          .filter((p) => !prevIdsRef.current.has(p.id))
          .map((p) => p.id);
        if (arrivals.length > 0) {
          if (!muted) playChime();
          setNewIds((prev) => {
            const next = new Set(prev);
            arrivals.forEach((id) => next.add(id));
            return next;
          });
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              arrivals.forEach((id) => next.delete(id));
              return next;
            });
          }, 3000);
        }
      }
      prevIdsRef.current = currentIds;
      firstLoadRef.current = false;
      setPhotos(data);
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    loadEvent();
    api.get("/pix-info").then((r) => setPix(r.data));
  }, [id]);

  useEffect(() => {
    if (!event?.slug) return;
    loadPhotos();
    const int = setInterval(loadPhotos, 5000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [event?.slug, muted]);

  const shareStory = async (photo) => {
    try {
      toast.loading("Gerando imagem para Stories...", { id: "story-gen" });
      await downloadForStories(
        fileUrl(photo.storage_path),
        `story-${photo.guest_name || "foto"}.jpg`,
      );
      toast.success("Imagem baixada! Cole no seu Story ✨", { id: "story-gen" });
    } catch {
      toast.error("Não deu para gerar. Tente de novo.", { id: "story-gen" });
    }
  };

  const uploadFrame = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow same file re-selection
    if (!file) return;
    if (!file.type.includes("png")) {
      toast.error("Envie um arquivo PNG com transparência.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/events/${id}/frame`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Moldura enviada!");
      setEvent((ev) => ({ ...ev, frame_path: data.frame_path, frames: data.frames }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const deleteFrame = async (framePath) => {
    if (!confirm("Remover essa moldura?")) return;
    try {
      const { data } = await api.delete(
        `/events/${id}/frame?path=${encodeURIComponent(framePath)}`,
      );
      toast.success("Moldura removida");
      setEvent((ev) => ({
        ...ev,
        frames: data.frames,
        frame_path: data.frames[0] || null,
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyGuestLink = () => {
    const url = `${window.location.origin}/e/${event.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const downloadZip = async () => {
    try {
      const token = localStorage.getItem("gve_token");
      const res = await fetch(`${API}/events/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erro no download");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${event.slug}-fotos.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download iniciado!");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const downloadQr = async () => {
    try {
      const token = localStorage.getItem("gve_token");
      const res = await fetch(`${API}/events/${id}/qr`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-${event.slug}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao baixar QR");
    }
  };

  const downloadQrSheet = async () => {
    try {
      toast.loading("Gerando folha de adesivos...", { id: "qr-sheet" });
      const token = localStorage.getItem("gve_token");
      const res = await fetch(`${API}/events/${id}/qr-sheet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erro");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-${event.slug}-adesivos.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF pronto! 6 QR codes por página.", { id: "qr-sheet" });
    } catch (err) {
      toast.error(err.message || "Erro ao gerar PDF", { id: "qr-sheet" });
    }
  };

  if (!event)
    return (
      <div className="min-h-screen text-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-16 text-white/50">
          Carregando evento...
        </div>
      </div>
    );

  const guestUrl = `${window.location.origin}/e/${event.slug}`;
  const isPaid = event.payment_status === "approved" || event.payment_status === "waived";
  const qrUrl = `${API}/events/${id}/qr`;
  const token = localStorage.getItem("gve_token");

  return (
    <div className="min-h-screen text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
                Evento
              </span>
              {event.is_vip && (
                <span className="badge-vip">
                  <Sparkles className="w-3 h-3" /> VIP
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
              {event.name}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-white/50">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(event.date).toLocaleDateString("pt-BR")}
              </span>
              <span className="flex items-center gap-1">
                <Camera className="w-4 h-4" /> {photos.length} fotos
              </span>
              <span className="flex items-center gap-1">
                <Info className="w-4 h-4" /> Limite: {event.photo_limit}/convidado
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {event.status === "ended" && (
              <button
                onClick={() => setTestimonialOpen(true)}
                className="btn-neon"
                data-testid="open-testimonial-btn"
              >
                <MessageSquare className="w-4 h-4" /> Deixar depoimento
              </button>
            )}
            {isPaid && (
              <>
                <Link
                  to={`/e/${event.slug}/hall`}
                  target="_blank"
                  className="btn-cyan"
                  data-testid="open-hall-btn"
                  title="Hall da Fama"
                >
                  <Trophy className="w-4 h-4" /> Hall da Fama
                </Link>
                <Link
                  to={`/e/${event.slug}/slideshow`}
                  target="_blank"
                  className="btn-cyan"
                  data-testid="open-slideshow-btn"
                  title="Modo telão"
                >
                  <Tv className="w-4 h-4" /> Modo telão
                </Link>
              </>
            )}
            <button
              onClick={() => {
                primeAudio();
                setMuted((m) => !m);
              }}
              className="btn-cyan px-4"
              data-testid="toggle-chime"
              title={muted ? "Ativar sino" : "Silenciar sino"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={downloadZip}
              disabled={photos.length === 0}
              className="btn-cyan"
              data-testid="download-zip-btn"
            >
              <Download className="w-4 h-4" /> Baixar ZIP
            </button>
          </div>
        </div>

        {/* Payment banner */}
        {event.payment_status === "pending" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 glass rounded-2xl p-6 border-[#FF007F]/40"
            data-testid="pix-payment-box"
          >
            <div className="flex flex-wrap gap-6 items-center">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 text-[#FF007F] mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-[0.2em]">
                    Pagamento pendente
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl tracking-tight">
                  Pague R$ {(Number(event?.price || pix?.price || 49.9)).toFixed(2).replace(".", ",")} via PIX para ativar
                </h3>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex gap-2">
                    <span className="text-white/50 w-32">Chave PIX:</span>
                    <span className="font-mono">{pix?.key}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(pix?.key || "");
                        toast.success("Chave copiada!");
                      }}
                      className="text-[#00F0FF] hover:text-white transition-colors"
                      data-testid="copy-pix-key"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-white/50 w-32">Beneficiário:</span>
                    <span>{pix?.beneficiary}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-white/50 w-32">Valor:</span>
                    <span className="text-[#39FF14] font-semibold">
                      R$ {(Number(event?.price || pix?.price || 49.9)).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-white/40 mt-4 flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Após o pagamento, avise para
                  liberação. Aprovação manual em até algumas horas.
                </p>
              </div>

              <div className="w-40 h-40 rounded-2xl bg-white p-3 flex items-center justify-center">
                <div className="w-full h-full grid grid-cols-8 grid-rows-8 gap-[2px]">
                  {/* Decorative fake QR - actual PIX QR would be generated by MP */}
                  {Array.from({ length: 64 }).map((_, i) => {
                    const r = (i * 37) % 100;
                    return (
                      <div
                        key={i}
                        className={r < 55 ? "bg-black" : "bg-white"}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Retention banner — shown when event ended */}
        {event.status === "ended" && event.retention?.days_remaining !== null && (
          <div
            className={`mb-4 glass rounded-2xl p-5 flex flex-wrap gap-4 items-center ${
              event.retention.days_remaining <= 7 ? "border-yellow-500/40" : ""
            }`}
            data-testid="retention-banner"
          >
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-1">
                {event.retention.days_remaining <= 7 ? (
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                ) : (
                  <Clock className="w-4 h-4 text-[#00F0FF]" />
                )}
                <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                  Retenção das fotos
                </span>
              </div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {event.retention.expired
                  ? "Fotos apagadas"
                  : `Fotos disponíveis por mais ${event.retention.days_remaining} dia${
                      event.retention.days_remaining === 1 ? "" : "s"
                    }`}
              </h3>
              <p className="text-xs text-white/50 mt-1">
                As fotos são mantidas por 30 dias após o encerramento do evento.
                Baixe o ZIP antes disso para ficar com tudo!
              </p>
            </div>
            {!event.retention.expired && (
              <button
                onClick={downloadZip}
                disabled={photos.length === 0}
                className="btn-neon"
                data-testid="retention-download-btn"
              >
                <Download className="w-4 h-4" /> Baixar tudo agora
              </button>
            )}
          </div>
        )}

        {/* Testimonial CTA banner — prominent when event is ended */}
        {event.status === "ended" && (
          <div
            className="mb-8 rounded-2xl p-6 border border-[#FF007F]/50 relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,0,127,0.12), rgba(122,0,255,0.08))",
              boxShadow: "0 0 32px rgba(255,0,127,0.15)",
            }}
            data-testid="testimonial-cta-banner"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#FF007F]/25 blur-3xl pointer-events-none" />
            <div className="relative flex flex-wrap gap-4 items-center">
              <div className="w-14 h-14 rounded-full bg-[#FFD700]/15 border-2 border-[#FFD700]/60 flex items-center justify-center shrink-0">
                <MessageSquare className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#FFD700] font-bold">
                  Como foi sua experiência?
                </div>
                <h3 className="font-display font-black text-xl sm:text-2xl tracking-tighter mt-1">
                  Deixe seu depoimento
                </h3>
                <p className="text-sm text-white/70 mt-1">
                  Depois de aprovado, pode virar destaque na página inicial da
                  Festagram e ajudar outros anfitriões.
                </p>
              </div>
              <button
                onClick={() => setTestimonialOpen(true)}
                className="btn-neon"
                data-testid="cta-open-testimonial"
              >
                <MessageSquare className="w-4 h-4" /> Escrever depoimento
              </button>
            </div>
          </div>
        )}

        {/* Info: testimonial will appear later — shown when event is NOT ended */}
        {event.status !== "ended" && event.payment_status !== "pending" && (
          <div
            className="mb-8 glass rounded-2xl p-4 flex items-start gap-3 text-sm"
            data-testid="testimonial-hint"
          >
            <MessageSquare className="w-4 h-4 text-[#FF007F] mt-0.5 shrink-0" />
            <div className="text-white/70">
              <span className="text-white font-semibold">
                Deixar depoimento:
              </span>{" "}
              o botão "Escrever depoimento" vai aparecer aqui assim que o admin
              encerrar o evento. Aí você conta como foi a experiência e ela
              pode virar destaque na página inicial.
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left column: frame & QR */}
          <div className="lg:col-span-4 space-y-6">
            {/* Frame upload */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/40 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#FF007F]" />
                  </div>
                  <h3 className="font-display font-bold tracking-tight">
                    Molduras {(() => {
                      const f = event.frames || (event.frame_path ? [event.frame_path] : []);
                      const max = event.plan === "premium" || event.is_vip ? 3 : 1;
                      return `(${f.length}/${max})`;
                    })()}
                  </h3>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full font-bold ${
                    event.plan === "premium"
                      ? "bg-[#FF007F]/10 text-[#FF007F] border border-[#FF007F]/40"
                      : "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/40"
                  }`}
                  data-testid="plan-badge"
                >
                  {event.plan === "premium" ? "Premium" : "Básico"}
                </span>
              </div>

              {(() => {
                const framesList = event.frames || (event.frame_path ? [event.frame_path] : []);
                const maxFrames = event.plan === "premium" || event.is_vip ? 3 : 1;
                const canAddMore = framesList.length < maxFrames;
                return (
                  <>
                    {framesList.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {framesList.map((f, i) => (
                          <div
                            key={f}
                            className="relative aspect-[9/16] rounded-lg border border-white/10 bg-white/5 overflow-hidden group"
                            data-testid={`frame-slot-${i}`}
                          >
                            <img
                              src={fileUrl(f)}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                            <div className="absolute top-1 left-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-black/70 text-white">
                              #{i + 1}
                            </div>
                            {framesList.length > 1 || event.plan === "premium" ? (
                              <button
                                onClick={() => deleteFrame(f)}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-frame-${i}`}
                                title="Remover"
                              >
                                ✕
                              </button>
                            ) : null}
                          </div>
                        ))}
                        {Array.from({ length: Math.max(0, maxFrames - framesList.length) }).map((_, i) => (
                          <div
                            key={`empty-${i}`}
                            className="aspect-[9/16] rounded-lg border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-white/30 text-xs"
                          >
                            vazio
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        ref={framePreviewRef}
                        className="aspect-[9/16] max-h-64 mx-auto rounded-xl border border-dashed border-white/20 bg-white/5 relative overflow-hidden mb-4 flex items-center justify-center"
                      >
                        <div className="text-center px-6">
                          <Upload className="w-8 h-8 text-white/30 mx-auto mb-2" />
                          <p className="text-xs text-white/50">
                            PNG 9:16 com transparência
                          </p>
                        </div>
                      </div>
                    )}

                    {canAddMore ? (
                      <label
                        className="btn-neon w-full cursor-pointer"
                        data-testid="upload-frame-label"
                      >
                        <Upload className="w-4 h-4" />
                        {uploading
                          ? "Enviando..."
                          : framesList.length === 0
                          ? "Enviar moldura PNG"
                          : `Adicionar moldura ${framesList.length + 1}`}
                        <input
                          type="file"
                          accept="image/png"
                          onChange={uploadFrame}
                          className="hidden"
                          disabled={uploading}
                          data-testid="upload-frame-input"
                        />
                      </label>
                    ) : (
                      <div className="text-center text-xs text-white/40 py-2">
                        Limite de molduras atingido para este plano.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* QR code + share */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/40 flex items-center justify-center">
                  <QrCode className="w-4 h-4 text-[#00F0FF]" />
                </div>
                <h3 className="font-display font-bold tracking-tight">
                  QR code do evento
                </h3>
              </div>

              {isPaid ? (
                <>
                  <div className="aspect-square rounded-xl bg-white p-4 flex items-center justify-center mb-4">
                    <img
                      src={`${qrUrl}?t=${token}`}
                      alt="QR code"
                      className="w-full h-full object-contain"
                      onLoad={() => setQrLoaded(true)}
                      data-testid="event-qr-code"
                      onError={async (e) => {
                        try {
                          const res = await fetch(qrUrl, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const blob = await res.blob();
                          e.target.src = URL.createObjectURL(blob);
                        } catch {
                          /* no-op */
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={guestUrl}
                        className="input-dark text-xs flex-1"
                        data-testid="guest-link-input"
                      />
                      <button
                        onClick={copyGuestLink}
                        className="btn-cyan px-3"
                        data-testid="copy-guest-link"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={downloadQr}
                        className="btn-cyan flex-1 text-sm"
                        data-testid="download-qr-btn"
                      >
                        <Download className="w-4 h-4" /> QR
                      </button>
                      <button
                        onClick={downloadQrSheet}
                        className="btn-cyan flex-1 text-sm"
                        data-testid="download-qr-sheet-btn"
                        title="PDF com 6 QR codes por página para imprimir e recortar"
                      >
                        <FileText className="w-4 h-4" /> Adesivos PDF
                      </button>
                      <a
                        href={guestUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-cyan text-sm px-4"
                        data-testid="open-guest-link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-white/40 text-sm">
                  QR code disponível após aprovação do pagamento.
                </div>
              )}
            </div>
          </div>

          {/* Right column: Live gallery */}
          <div className="lg:col-span-8">
            <div className="glass rounded-2xl p-6 min-h-[400px]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-display font-bold text-xl tracking-tight">
                    Galeria ao vivo
                  </h3>
                  <p className="text-xs text-white/50 mt-1 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-ring" />
                    Atualiza automaticamente a cada 5 segundos
                  </p>
                </div>
                <span className="text-sm">
                  <span className="text-[#FF007F] font-bold text-lg">
                    {photos.length}
                  </span>{" "}
                  <span className="text-white/50">fotos</span>
                </span>
              </div>

              {photos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Camera className="w-10 h-10 text-white/20 mb-3" />
                  <p className="text-white/50 text-sm">
                    Nenhuma foto ainda. Compartilhe o QR code com seus convidados!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  <AnimatePresence>
                    {photos.map((p) => (
                      <motion.button
                        key={p.id}
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.35 }}
                        onClick={() => setSelected(p)}
                        className={`aspect-square rounded-lg overflow-hidden relative group text-left ${
                          newIds.has(p.id) ? "ring-2 ring-[#FF007F] glow-pink" : ""
                        }`}
                        data-testid={`photo-${p.id}`}
                      >
                        <img
                          src={fileUrl(p.storage_path)}
                          alt={p.guest_name}
                          className="w-full h-full object-cover"
                        />
                        {newIds.has(p.id) && (
                          <div className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#FF007F] text-white font-bold">
                            Novo
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="text-xs text-white truncate">
                            {p.guest_name}
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="event-photo-lightbox"
        >
          <div
            className="max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fileUrl(selected.storage_path)}
              alt=""
              className="w-full h-auto rounded-2xl"
            />
            <div className="text-center mt-4 text-white/70 text-sm">
              por <span className="text-[#FF007F]">{selected.guest_name}</span>
            </div>
            <div className="text-center mt-4 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => shareStory(selected)}
                className="btn-neon text-sm"
                data-testid="event-share-story-btn"
              >
                <Instagram className="w-4 h-4" /> Baixar para Stories
              </button>
              <a
                href={fileUrl(selected.storage_path)}
                download={`foto-${selected.guest_name || "convidado"}.jpg`}
                className="btn-cyan text-sm"
                target="_blank"
                rel="noreferrer"
              >
                <Download className="w-4 h-4" /> Original
              </a>
              <button
                className="text-sm text-white/60 hover:text-white transition-colors px-3 py-2"
                onClick={() => setSelected(null)}
                data-testid="event-close-lightbox"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <TestimonialModal
        eventId={id}
        eventName={event.name}
        open={testimonialOpen}
        onClose={() => setTestimonialOpen(false)}
      />
    </div>
  );
}
